import { useState } from 'react';

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What services does Finance Planning Wealth offer?',
      answer: 'We offer comprehensive financial planning services including investment management, retirement planning, tax planning, estate planning, and wealth preservation strategies. Our personalised approach ensures that each client receives tailored advice based on their unique financial situation and goals.'
    },
    {
      question: 'How do I get started with financial planning?',
      answer: 'Getting started is easy! Simply contact us to schedule a free initial consultation. During this meeting, we\'ll discuss your financial goals, current situation, and how our services can help you achieve your objectives. There\'s no obligation, and it\'s a great opportunity to see if we\'re the right fit for you.'
    },
    {
      question: 'What are your fees?',
      answer: 'Our fee structure is transparent and tailored to your needs. We offer various pricing models including fixed fees, hourly rates, and percentage-based fees depending on the services required. During your initial consultation, we\'ll provide a clear breakdown of costs with no hidden charges.'
    },
    {
      question: 'How often will we meet to review my financial plan?',
      answer: 'We typically recommend quarterly or semi-annual reviews, but the frequency depends on your individual needs and preferences. Regular reviews ensure your financial plan stays aligned with your goals and adapts to any life changes or market conditions. We\'re also available for ad-hoc consultations whenever needed.'
    },
    {
      question: 'Is my financial information secure?',
      answer: 'Absolutely. We take data security very seriously and employ bank-level encryption and security measures to protect your personal and financial information. We\'re fully compliant with UK-GDPR regulations and follow strict confidentiality protocols. Your privacy and security are our top priorities.'
    },
    {
      question: 'Do you work with clients outside of London?',
      answer: 'Yes! While we\'re based in London, we work with clients throughout the UK and internationally. We offer virtual consultations via video conferencing, making it convenient for clients anywhere to access our services. Our digital platforms ensure seamless communication and document sharing regardless of location.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <section id="faq" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1B3A5F] mb-4">
              Frequently Asked <span className="text-[#F5A623]">Questions</span>
            </h2>
            <p className="text-lg text-gray-600">
              Find answers to common questions about our services
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-[#F5A623] transition-colors"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-base font-semibold text-[#1B3A5F] pr-4">
                    {faq.question}
                  </h3>
                  <div className="flex-shrink-0 w-8 h-8 bg-[#F5A623] rounded-full flex items-center justify-center">
                    <i
                      className={`ri-${openIndex === index ? 'subtract' : 'add'}-line text-white text-lg transition-transform duration-300`}
                    ></i>
                  </div>
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    openIndex === index ? 'max-h-96' : 'max-h-0'
                  }`}
                >
                  <div className="px-6 pb-5 text-gray-600 text-sm leading-relaxed">
                    {faq.answer}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center bg-gradient-to-br from-gray-50 to-white rounded-xl p-8 border border-gray-100">
            <h3 className="text-xl font-bold text-[#1B3A5F] mb-3">
              Still have questions?
            </h3>
            <p className="text-gray-600 text-sm mb-6">
              We're here to help. Contact us for more information or to schedule a consultation.
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) {
                  const offset = 80;
                  const elementPosition = element.getBoundingClientRect().top;
                  const offsetPosition = elementPosition + window.pageYOffset - offset;
                  window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
              }}
              className="bg-[#F5A623] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#E09612] transition-colors shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
            >
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </>
  );
};

export default FAQ;
